// src/pages/Billing.tsx
import React, { useEffect, useState } from "react";
import { api } from "../api/client";
import { CreditCard, ShieldCheck, Clock } from "lucide-react";

const WORKSPACE_ID =
  process.env.REACT_APP_WORKSPACE_ID || "demo-workspace";

type SubscriptionStatus = {
  plan: "free" | "pro";
  effectivePlan: "free" | "pro" | "trial";
  trialDaysLeft: number;
  trialEndsAt?: string;
  subscriptionEndsAt?: string;
  isTrialActive: boolean;
  isPro: boolean;
};

type BillingCycle = "monthly" | "yearly";

export default function Billing() {
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [preferredCycle, setPreferredCycle] = useState<BillingCycle | null>(null);
  const [autoStartCheckout, setAutoStartCheckout] = useState(false);
  const [checkoutNotice, setCheckoutNotice] = useState<
    null | { type: "success" | "cancel"; message: string }
  >(null);
  const isTrial = status?.effectivePlan === "trial";
  const isPro = status?.effectivePlan === "pro";

  useEffect(() => {
    let mounted = true;
    async function loadStatus() {
      try {
        setLoading(true);
        const data = await api.getSubscriptionStatus(WORKSPACE_ID);
        if (mounted) {
          setStatus(data);
        }
      } catch (err) {
        if (mounted) {
          setError("Unable to load subscription status");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadStatus();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("success");
    const canceled = params.get("canceled");
    const autostart = params.get("autostart");
    const billingCycleParam = params.get("billingCycle");
    const billingCycle: BillingCycle | null =
      billingCycleParam === "monthly" || billingCycleParam === "yearly"
        ? billingCycleParam
        : null;

    if (billingCycle) {
      setPreferredCycle(billingCycle);
    }

    if (autostart === "1" && billingCycle) {
      setAutoStartCheckout(true);
    }

    if (success === "1") {
      setCheckoutNotice({
        type: "success",
        message: "Checkout completed. Refreshing your subscription status…",
      });
      api
        .getSubscriptionStatus(WORKSPACE_ID)
        .then((data) => setStatus(data))
        .catch(() => setError("Unable to refresh subscription status"));
    } else if (canceled === "1") {
      setCheckoutNotice({
        type: "cancel",
        message: "Checkout was canceled. You can upgrade anytime.",
      });
    }

    if (success === "1" || canceled === "1" || autostart === "1") {
      const url = new URL(window.location.href);
      url.searchParams.delete("success");
      url.searchParams.delete("canceled");
      url.searchParams.delete("autostart");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  useEffect(() => {
    if (!autoStartCheckout || loading || checkingOut || isPro) return;
    setAutoStartCheckout(false);
    void startCheckout(preferredCycle || undefined);
  }, [autoStartCheckout, loading, checkingOut, isPro, preferredCycle]);

  async function startCheckout(billingCycle?: BillingCycle) {
    try {
      setCheckingOut(true);
      const result = await api.createCheckoutSession(
        WORKSPACE_ID,
        billingCycle ? { billingCycle } : undefined,
      );
      if (result?.url) {
        window.location.href = result.url;
      } else {
        setError("Stripe checkout could not be started");
      }
    } catch (err) {
      setError("Stripe checkout could not be started");
    } finally {
      setCheckingOut(false);
    }
  }

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-6">
      {checkoutNotice && (
        <div
          className={`rounded-lg px-4 py-3 text-sm font-medium border ${
            checkoutNotice.type === "success"
              ? "bg-green-50 text-green-700 border-green-200"
              : "bg-yellow-50 text-yellow-700 border-yellow-200"
          }`}
        >
          {checkoutNotice.message}
        </div>
      )}
      <div className="flex items-center gap-3">
        <CreditCard className="text-indigo-600" />
        <div>
          <h1 className="text-3xl font-bold">Billing</h1>
          <p className="text-gray-600">
            Manage your RetroRail subscription and upgrade to unlock AI grouping,
            unlimited channels, and priority Slack support.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-white rounded-xl shadow p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Current status</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-indigo-50 text-indigo-700">
                  {isPro ? "Pro" : isTrial ? "Trial" : "Free"}
                </span>
                {isTrial && (
                  <span className="flex items-center gap-1 text-sm text-gray-600">
                    <Clock size={16} />
                    {status?.trialDaysLeft || 0} days left
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Workspace</p>
              <p className="font-medium text-gray-800">{WORKSPACE_ID}</p>
            </div>
          </div>

          <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
            <div className="flex items-center gap-2">
              <ShieldCheck className="text-indigo-600" />
              <div>
                <p className="font-semibold text-gray-800">Pro includes</p>
                <p className="text-sm text-gray-600">
                  AI grouping and summaries, unlimited sessions, multi-team configs, and advanced Slack workflows.
                </p>
              </div>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
              <li>• Unlimited channels and teams</li>
              <li>• AI grouping and action drafts</li>
              <li>• Priority Slack support</li>
              <li>• Exportable reports</li>
            </ul>
          </div>

          <div className="flex items-center gap-3">
            <button
              disabled={checkingOut || isPro}
              onClick={() => startCheckout(preferredCycle || undefined)}
              className={`px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
                isPro
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700"
              }`}
            >
              {isPro
                ? "You are on Pro"
                : checkingOut
                  ? "Redirecting..."
                  : preferredCycle === "yearly"
                    ? "Upgrade to Pro (Yearly)"
                    : preferredCycle === "monthly"
                      ? "Upgrade to Pro (Monthly)"
                      : "Upgrade to Pro"}
            </button>
            {isTrial && (
              <p className="text-sm text-gray-600">
                Trial ends in {status?.trialDaysLeft} days. Keep your workflows running with Pro.
              </p>
            )}
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}
          {loading && <div className="text-gray-500 text-sm">Loading subscription...</div>}
        </div>

        <div className="bg-white rounded-xl shadow p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Trial</p>
              <p className="text-2xl font-bold">14 days</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Price</p>
              <p className="text-2xl font-bold">$29</p>
              <p className="text-sm text-gray-500">per workspace / mo</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            New workspaces start with a 14-day free trial. You can keep using Free after that, or upgrade anytime.
          </p>
          <div className="pt-2 border-t text-sm text-gray-500 space-y-1">
            <p>Trial active: {status?.isTrialActive ? "Yes" : "No"}</p>
            <p>Pro active: {status?.isPro ? "Yes" : "No"}</p>
            {status?.subscriptionEndsAt && (
              <p>Paid until: {new Date(status.subscriptionEndsAt).toLocaleDateString()}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
